import { DropIcon, WarningIcon } from '@phosphor-icons/react';
import { LIQUIDITY_AVAILABILITY_FLAG } from '@/lib/investments/liquidity';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useFeatureFlags } from '@/lib/telemetry';
import type { InvestmentItem } from '@/types';

export function availabilityText(inv: InvestmentItem): string | null {
  if (inv.blockedContradiction) {
    return 'O banco informa que nada está disponível para resgate no momento, apesar da carência cumprida.';
  }
  if (inv.liquidityBucket === 'daily') {
    return inv.settlementUnknown
      ? 'Disponível para resgate hoje — o prazo de liquidação (D+N) não é informado pelo Open Finance.'
      : 'Disponível para uso hoje.';
  }
  if (inv.redeemableAt) {
    // UTC-pinned: connectors anchor these dates at/near midnight UTC, and browser-local
    // rendering (UTC−3) would promise the money one day early.
    return `Disponível em ${new Date(inv.redeemableAt).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}.`;
  }
  return null;
}

export function InvestmentAvailabilityNotice({ inv }: { inv: InvestmentItem }) {
  const enabled = useFeatureFlags(LIQUIDITY_AVAILABILITY_FLAG);
  const formatCurrency = useFormatCurrency();

  if (!enabled || inv.status === 'TOTAL_WITHDRAWAL') return null;
  const text = availabilityText(inv);
  if (!text) return null;

  const Icon = inv.blockedContradiction ? WarningIcon : DropIcon;

  return (
    <p
      className={`flex items-start gap-2 rounded-xl px-3 py-2 text-sm ${
        inv.blockedContradiction
          ? 'bg-warning-surface text-warning-text'
          : 'bg-muted text-text-secondary'
      }`}
    >
      <Icon size={14} className="mt-0.5 shrink-0" />
      <span>
        {text}
        {inv.partialAvailability && inv.amountWithdrawal != null && (
          <> Disponível agora: {formatCurrency(inv.amountWithdrawal)} de {formatCurrency(inv.balance)}.</>
        )}
      </span>
    </p>
  );
}
